import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useEnhancedBrandStore } from '../../../stores/enhancedBrandStore'
import { AmazonPresence } from '../../../types/brand'
import echarts from '../../../lib/echarts'

const AMAZON_COLORS = {
  none: '#e2e8f0',
  available: '#059669',
  brand_store: '#2563eb',
  limited: '#d97706',
  unknown: '#71717a'
}

const AMAZON_LABELS = {
  none: 'Not on Amazon',
  available: 'Available',
  brand_store: 'Brand Store',
  limited: 'Limited',
  unknown: 'Unknown'
}

export function InteractiveAmazonChart() {
  const { filteredBrands, allBrands, filters, setAmazonPresenceFilter } = useEnhancedBrandStore()

  const chartData = useMemo(() => {
    // Get counts from filtered data
    const filteredCounts = filteredBrands.reduce((acc, brand) => {
      if (brand.amazon_presence in AMAZON_LABELS) {
        acc[brand.amazon_presence] = (acc[brand.amazon_presence] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    // Get total counts for context
    const totalCounts = allBrands.reduce((acc, brand) => {
      if (brand.amazon_presence in AMAZON_LABELS) {
        acc[brand.amazon_presence] = (acc[brand.amazon_presence] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    return Object.entries(filteredCounts).map(([key, value]) => ({
      name: AMAZON_LABELS[key as AmazonPresence],
      value,
      key: key as AmazonPresence,
      totalValue: totalCounts[key] || 0,
      itemStyle: {
        color: AMAZON_COLORS[key as AmazonPresence],
        shadowBlur: filters.amazonPresence.length > 0 && filters.amazonPresence.includes(key as AmazonPresence) ? 8 : 0,
        shadowColor: filters.amazonPresence.length > 0 && filters.amazonPresence.includes(key as AmazonPresence) ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
        borderWidth: filters.amazonPresence.length > 0 && filters.amazonPresence.includes(key as AmazonPresence) ? 2 : 0,
        borderColor: '#ffffff'
      }
    }))
  }, [filteredBrands, allBrands, filters.amazonPresence])

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const dataItem = chartData.find(item => item.name === params.name)
        if (!dataItem) return params.name
        
        const totalPercentage = ((dataItem.totalValue / allBrands.length) * 100).toFixed(1)
        const filteredPercentage = filteredBrands.length > 0 ? ((params.value / filteredBrands.length) * 100).toFixed(1) : '0'
        
        const hasFilters = filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.productCategories.length > 0 || 
                          filters.states.length > 0
        
        if (hasFilters) {
          return `<strong>${params.name}</strong><br/>` +
                 `${params.value} of ${dataItem.totalValue} brands<br/>` +
                 `<span style="color: #059669;">${totalPercentage}% of all brands</span><br/>` +
                 `<span style="color: #6b7280;">${filteredPercentage}% of filtered view</span>`
        } else {
          return `<strong>${params.name}</strong><br/>${params.value} brands (${totalPercentage}%)`
        }
      }
    },
    series: [
      {
        type: 'pie',
        radius: ['30%', '70%'],
        center: ['50%', '50%'],
        data: chartData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        label: {
          formatter: (params: any) => {
            const percentage = ((params.value / allBrands.length) * 100).toFixed(1)
            return `${params.name}\n${percentage}%`
          },
          fontSize: 12,
          color: '#1f2937',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 4,
          padding: [4, 8],
          borderWidth: 1,
          borderColor: '#e5e7eb'
        }
      }
    ]
  }

  const handleChartClick = (params: any) => {
    const clickedKey = chartData.find(item => item.name === params.name)?.key
    if (!clickedKey) return

    const currentFilters = filters.amazonPresence
    const isSelected = currentFilters.includes(clickedKey)

    if (isSelected) {
      // Remove from filter
      setAmazonPresenceFilter(currentFilters.filter(f => f !== clickedKey))
    } else {
      // Add to filter
      setAmazonPresenceFilter([...currentFilters, clickedKey])
    }
  }

  const clearFilter = (filterValue: AmazonPresence) => {
    setAmazonPresenceFilter(filters.amazonPresence.filter(f => f !== filterValue))
  }

  const onEvents = {
    click: handleChartClick
  }

  const hasActiveFilters = filters.amazonPresence.length > 0 || 
                          filters.manufacturingVerification.length > 0 || 
                          filters.priceTier.length > 0 || 
                          filters.productCategories.length > 0 || 
                          filters.states.length > 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Chart */}
      <div className="space-y-3">
        {hasActiveFilters && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-xs font-medium">
              FILTERED VIEW
            </div>
            <span className="text-gray-600 dark:text-gray-400">
              Showing {filteredBrands.length} of {allBrands.length} brands
            </span>
          </div>
        )}
        <div className="h-64">
          <ReactECharts
            option={option}
            style={{ height: '100%', width: '100%' }}
            onEvents={onEvents}
            echarts={echarts}
          />
        </div>
      </div>

      {/* Active Filters */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Amazon Presence Breakdown
          </h4>
          {Object.entries(AMAZON_LABELS).map(([key, label]) => {
            const isSelected = filters.amazonPresence.includes(key as AmazonPresence)
            const dataItem = chartData.find(item => item.key === key)
            const filteredCount = dataItem?.value || 0
            const totalCount = dataItem?.totalValue || 0
            const totalPercentage = totalCount > 0 ? ((totalCount / allBrands.length) * 100).toFixed(1) : '0'
            
            if (totalCount === 0) return null // Don't show categories with no data
            
            return (
              <div
                key={key}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                  isSelected
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                onClick={() => {
                  if (isSelected) {
                    clearFilter(key as AmazonPresence)
                  } else {
                    setAmazonPresenceFilter([...filters.amazonPresence, key as AmazonPresence])
                  }
                }}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: AMAZON_COLORS[key as AmazonPresence] }}
                  />
                  <div>
                    <div className={`text-sm font-medium ${
                      isSelected 
                        ? 'text-blue-700 dark:text-blue-300' 
                        : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {label}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {hasActiveFilters && filteredCount !== totalCount 
                        ? `${filteredCount} of ${totalCount} brands • ${totalPercentage}% of total`
                        : `${totalCount} brands • ${totalPercentage}% of total`
                      }
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {isSelected ? '✓ Selected' : 'Click to filter'}
                </div>
              </div>
            )
          })}
          
          {filters.amazonPresence.length > 0 && (
            <button
              onClick={() => setAmazonPresenceFilter([])}
              className="w-full mt-3 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-2 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              Clear all Amazon filters
            </button>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            💡 <strong>Tip:</strong> Click any segment in the pie chart to filter brands by that Amazon presence type.
          </p>
        </div>
      </div>
    </div>
  )
}